import React, { useMemo, useState } from 'react';
import {
  Award, Check, X, Clock, RotateCcw, Eye, ChevronLeft, BookOpen, Trophy, AlertCircle,
} from 'lucide-react';
import type { AdminRealm } from '@/lib/adminAuth';
import type { QuizAnswerRecord, QuizAttempt, QuizQuestion, TrainingProgress } from '@/data/trainingTypes';
import { PASSING_SCORE, QUIZ_SIZE } from '@/data/trainingTypes';
import { QUIZ_BANK, BANK_SIZE } from '@/data/trainingQuiz';
import { recordQuizAttempt, shuffle } from '@/lib/trainingProgress';
import { sectionIndexById } from '@/data/trainingAcademy';

interface ActiveQuestion {
  q: QuizQuestion;
  choices: string[]; // shuffled
  correctText: string;
}

interface Props {
  realm: AdminRealm;
  progress: TrainingProgress;
  onProgress: (p: TrainingProgress) => void;
  onExit: () => void;
  onReviewSection: (sectionIdx: number) => void;
  onViewCertificate: () => void;
}

type Phase = 'start' | 'quiz' | 'results' | 'review';

function buildQuiz(): ActiveQuestion[] {
  return shuffle(QUIZ_BANK)
    .slice(0, QUIZ_SIZE)
    .map((q) => ({ q, choices: shuffle(q.choices), correctText: q.choices[0] }));
}

const diffBadge: Record<string, string> = {
  easy: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/25',
  medium: 'bg-sky-400/10 text-sky-300 border-sky-400/25',
  advanced: 'bg-amber-400/10 text-amber-300 border-amber-400/25',
};

const QuizEngine: React.FC<Props> = ({ realm, progress, onProgress, onExit, onReviewSection, onViewCertificate }) => {
  const [phase, setPhase] = useState<Phase>('start');
  const [questions, setQuestions] = useState<ActiveQuestion[]>([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<(string | null)[]>([]);
  const [startTs, setStartTs] = useState(0);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);

  const begin = () => {
    setQuestions(buildQuiz());
    setPicked(Array(QUIZ_SIZE).fill(null));
    setIdx(0);
    setStartTs(Date.now());
    setPhase('quiz');
  };

  const answer = (choice: string) => {
    const nextPicked = [...picked];
    nextPicked[idx] = choice;
    setPicked(nextPicked);
    setTimeout(() => {
      if (idx < questions.length - 1) {
        setIdx(idx + 1);
      } else {
        finish(nextPicked);
      }
    }, 250);
  };

  const finish = (finalPicked: (string | null)[]) => {
    const answers: QuizAnswerRecord[] = questions.map((aq, i) => ({
      qid: aq.q.id,
      topic: aq.q.topic,
      topicLabel: aq.q.topicLabel,
      question: aq.q.q,
      chosen: finalPicked[i] || '(no answer)',
      correct: aq.correctText,
      explain: aq.q.explain,
      wasCorrect: finalPicked[i] === aq.correctText,
      difficulty: aq.q.difficulty,
    }));
    const correctCount = answers.filter((a) => a.wasCorrect).length;
    const scorePct = Math.round((correctCount / questions.length) * 100);
    const att: QuizAttempt = {
      id: `att-${Date.now()}`,
      dateISO: new Date().toISOString(),
      scorePct,
      correctCount,
      total: questions.length,
      passed: scorePct >= PASSING_SCORE,
      durationSec: Math.round((Date.now() - startTs) / 1000),
      answers,
    };
    setAttempt(att);
    onProgress(recordQuizAttempt(realm, att));
    setPhase('results');
  };

  const topicBreakdown = useMemo(() => {
    if (!attempt) return [];
    const map: Record<string, { label: string; right: number; total: number }> = {};
    attempt.answers.forEach((a) => {
      if (!map[a.topic]) map[a.topic] = { label: a.topicLabel, right: 0, total: 0 };
      map[a.topic].total += 1;
      if (a.wasCorrect) map[a.topic].right += 1;
    });
    return Object.entries(map).map(([topic, v]) => ({ topic, ...v }));
  }, [attempt]);

  const fmtDur = (s: number) => `${Math.floor(s / 60)}m ${s % 60}s`;

  // ── START ──────────────────────────────────────────────────────────────
  if (phase === 'start') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-14 text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl border border-[#C9A961]/40 bg-[#C9A961]/10">
          <Award className="h-9 w-9 text-[#E4C77F]" />
        </div>
        <div className="text-[11px] font-medium uppercase tracking-[0.4em] text-[#C9A961]">Certification</div>
        <h1 className="mt-3 text-3xl font-light tracking-tight text-white sm:text-5xl">Social Media Hub Certification Quiz</h1>
        <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-white/55">
          {QUIZ_SIZE} questions drawn at random from a bank of {BANK_SIZE}, covering every section of the course.
          Answer order is shuffled on every attempt — no two quizzes look the same.
        </p>
        <div className="mx-auto mt-8 grid max-w-md grid-cols-3 gap-3">
          {[
            { v: `${QUIZ_SIZE}`, l: 'Questions' },
            { v: `${PASSING_SCORE}%`, l: 'To pass' },
            { v: '∞', l: 'Retakes' },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-2xl font-light text-[#E4C77F]">{s.v}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">{s.l}</div>
            </div>
          ))}
        </div>
        {progress.attempts.length > 0 && (
          <p className="mt-6 text-xs text-white/40">
            Previous attempts: {progress.attempts.length} · Best score:{' '}
            {Math.max(...progress.attempts.map((a) => a.scorePct))}%
          </p>
        )}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={begin}
            className="rounded-full bg-[#C9A961] px-8 py-3 text-sm font-semibold text-black shadow-lg shadow-[#C9A961]/25 transition-all hover:bg-[#E4C77F]"
          >
            Begin quiz
          </button>
          <button onClick={onExit} className="rounded-full border border-white/15 px-6 py-3 text-sm text-white/60 hover:text-white">
            Back to Academy
          </button>
        </div>
      </div>
    );
  }

  // ── QUIZ ───────────────────────────────────────────────────────────────
  if (phase === 'quiz') {
    const aq = questions[idx];
    const chosen = picked[idx];
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between text-xs text-white/50">
            <span>Question {idx + 1} of {questions.length}</span>
            <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${diffBadge[aq.q.difficulty]}`}>
              {aq.q.difficulty}
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#C9A961] to-[#E4C77F] transition-all duration-300"
              style={{ width: `${((idx + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="mb-1 text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">{aq.q.topicLabel}</div>
        <h2 className="text-xl font-light leading-snug text-white sm:text-2xl">{aq.q.q}</h2>
        <div className="mt-6 space-y-2.5">
          {aq.choices.map((c) => (
            <button
              key={c}
              disabled={chosen !== null}
              onClick={() => answer(c)}
              className={`w-full rounded-2xl border px-5 py-4 text-left text-sm transition-all duration-200 ${
                chosen === c
                  ? 'border-[#C9A961] bg-[#C9A961]/15 text-[#E4C77F]'
                  : 'border-white/15 bg-white/[0.03] text-white/80 hover:border-[#C9A961]/50 hover:bg-white/[0.06]'
              } ${chosen !== null && chosen !== c ? 'opacity-40' : ''}`}
            >
              {c}
            </button>
          ))}
        </div>
        <p className="mt-6 text-center text-[11px] text-white/35">Select an answer to continue — answers lock immediately.</p>
      </div>
    );
  }

  // ── RESULTS ────────────────────────────────────────────────────────────
  if (phase === 'results' && attempt) {
    const passed = attempt.passed;
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <div className="text-center">
          <div className={`mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border ${
            passed ? 'border-emerald-400/50 bg-emerald-400/10' : 'border-amber-400/50 bg-amber-400/10'
          }`}>
            {passed ? <Trophy className="h-9 w-9 text-emerald-300" /> : <AlertCircle className="h-9 w-9 text-amber-300" />}
          </div>
          <h1 className="text-3xl font-light text-white sm:text-4xl">
            {passed ? 'Certification earned' : 'Not quite — try again'}
          </h1>
          <div className={`mt-3 text-6xl font-light ${passed ? 'text-emerald-300' : 'text-amber-300'}`}>{attempt.scorePct}%</div>
          <p className="mt-2 text-sm text-white/50">
            {passed
              ? `You passed the ${PASSING_SCORE}% threshold. The Social Media Hub Certification is yours.`
              : `You need ${PASSING_SCORE}% to pass. Every retake draws a fresh set of questions.`}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { icon: <Check className="h-4 w-4 text-emerald-300" />, v: attempt.correctCount, l: 'Correct' },
            { icon: <X className="h-4 w-4 text-red-300" />, v: attempt.total - attempt.correctCount, l: 'Incorrect' },
            { icon: <Clock className="h-4 w-4 text-sky-300" />, v: fmtDur(attempt.durationSec), l: 'Time taken' },
            { icon: <Award className="h-4 w-4 text-[#E4C77F]" />, v: passed ? 'PASS' : 'FAIL', l: 'Status' },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-center">
              <div className="mx-auto mb-1.5 flex justify-center">{s.icon}</div>
              <div className="text-lg font-medium text-white">{s.v}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Topic performance */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <div className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-[#C9A961]">Performance by topic</div>
          <div className="space-y-3">
            {topicBreakdown.map((t) => (
              <div key={t.topic} className="flex items-center gap-3">
                <span className="w-36 shrink-0 truncate text-xs text-white/70">{t.label}</span>
                <div className="h-2 flex-1 rounded-full bg-white/10">
                  <div
                    className={`h-full rounded-full ${t.right === t.total ? 'bg-emerald-400/70' : t.right === 0 ? 'bg-red-400/70' : 'bg-[#C9A961]'}`}
                    style={{ width: `${(t.right / t.total) * 100}%` }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right text-xs text-white/50">{t.right}/{t.total}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => setPhase('review')}
            className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm text-white/80 hover:border-[#C9A961]/50 hover:text-white"
          >
            <Eye className="h-4 w-4" /> Review answers
          </button>
          {passed ? (
            <button
              onClick={onViewCertificate}
              className="flex items-center gap-2 rounded-full bg-[#C9A961] px-6 py-3 text-sm font-semibold text-black hover:bg-[#E4C77F]"
            >
              <Award className="h-4 w-4" /> View certificate
            </button>
          ) : (
            <button
              onClick={begin}
              className="flex items-center gap-2 rounded-full bg-[#C9A961] px-6 py-3 text-sm font-semibold text-black hover:bg-[#E4C77F]"
            >
              <RotateCcw className="h-4 w-4" /> Retake with new questions
            </button>
          )}
          <button onClick={onExit} className="rounded-full border border-white/15 px-6 py-3 text-sm text-white/60 hover:text-white">
            Back to Academy
          </button>
        </div>
      </div>
    );
  }

  // ── REVIEW ─────────────────────────────────────────────────────────────
  if (phase === 'review' && attempt) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <button
          onClick={() => setPhase('results')}
          className="mb-6 flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/60 hover:text-white"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back to results
        </button>
        <h1 className="text-2xl font-light text-white sm:text-3xl">Answer review</h1>
        <p className="mt-2 text-sm text-white/50">Every question, your answer, the correct answer, and why.</p>
        <div className="mt-8 space-y-5">
          {attempt.answers.map((a, i) => (
            <div
              key={a.qid}
              className={`rounded-2xl border p-5 ${a.wasCorrect ? 'border-emerald-400/25 bg-emerald-400/[0.04]' : 'border-red-400/25 bg-red-400/[0.04]'}`}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  a.wasCorrect ? 'bg-emerald-400/20 text-emerald-300' : 'bg-red-400/20 text-red-300'
                }`}>{i + 1}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961]">{a.topicLabel}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider ${diffBadge[a.difficulty]}`}>
                  {a.difficulty}
                </span>
              </div>
              <p className="text-sm font-medium text-white">{a.question}</p>
              <div className="mt-3 space-y-1.5 text-xs">
                <div className={`flex items-start gap-2 ${a.wasCorrect ? 'text-emerald-300' : 'text-red-300'}`}>
                  {a.wasCorrect ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                  Your answer: {a.chosen}
                </div>
                {!a.wasCorrect && (
                  <div className="flex items-start gap-2 text-emerald-300">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Correct answer: {a.correct}
                  </div>
                )}
              </div>
              <p className="mt-3 rounded-xl bg-white/[0.04] p-3 text-xs leading-relaxed text-white/60">{a.explain}</p>
              <button
                onClick={() => onReviewSection(Math.max(0, sectionIndexById(a.topic)))}
                className="mt-3 flex items-center gap-1.5 text-xs text-[#E4C77F] hover:underline"
              >
                <BookOpen className="h-3.5 w-3.5" /> Revisit the "{a.topicLabel}" section
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default QuizEngine;
